import {
    type KeyboardEvent,
    type ReactNode,
    type Ref,
    useCallback,
    useMemo,
    useState,
} from "react";
import type { RowData } from "@tanstack/react-table";
import classNames from "classnames";
import { styleWithVars } from "@lib/cssVars";
import { CellTooltipHost } from "./cells/CellTooltipHost";
import {
    DataGridContext,
    type DataGridContextValue,
    useDataGridContext,
} from "./DataGridContext";
import { isEditableTarget } from "./focus/editableTarget";
import { createGridController } from "./focus/gridController";
import { matchesShortcut, normalizeKey } from "./focus/keyMap";
import { getFocusedAddress } from "./focus/navigation";
import { useAutoFocus } from "./focus/useAutoFocus";
import { Grid } from "./grid/Grid";
import { HEADER_ROW_HEIGHT, ROW_HEIGHT_BY_DENSITY } from "./layout/density";
import { DataGridRangeActionSlot } from "./features/range/RangeActionSlot";
import { DataGridSelectAllMatching } from "./features/selection/SelectAllMatching";
import { DataGridEmpty } from "./states/Empty";
import { DataGridError } from "./states/Error";
import { DataGridGate } from "./states/Gate";
import { getStatusFlags } from "./states/statusFlags";
import { DataGridColumnSettings } from "./toolbar/ColumnSettings";
import { DataGridFilterChips } from "./toolbar/FilterChips";
import { DataGridPager } from "./toolbar/Pager";
import { DataGridSearch } from "./toolbar/Search";
import type { DataGridInstance } from "./types";
import { useConstant } from "@hooks/useConstant";
import { type DataGridHandle, useTableHandle } from "./useTableHandle";
import styles from "./DataGrid.module.scss";

export type { DataGridHandle } from "./useTableHandle";

/** The line above the header row: `"none"` when a strip or card above already draws one. */
export type DataGridTopBorder = "divider" | "none";

export interface DataGridProps<TData extends RowData> {
    table: DataGridInstance<TData>;
    "aria-label"?: string;
    "aria-labelledby"?: string;
    className?: string;
    /** Parts in layout order. Defaults to `Grid`, `Empty`, and `Error`. */
    children?: ReactNode;
    /**
     * Focuses the first row once rows are ready. `true` focuses once; a
     * string focuses again whenever it changes (pass the open record's id).
     */
    autoFocus?: boolean | string;
    /**
     * The divider above the header row, in every state, like ViewTable's.
     * Defaults to `"divider"`; never draw it from consumer CSS.
     */
    topBorder?: DataGridTopBorder;
    ref?: Ref<DataGridHandle>;
}

const DataGridRoot = <TData extends RowData>({
    table: instance,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    className,
    children,
    autoFocus,
    topBorder = "divider",
    ref,
}: Readonly<DataGridProps<TData>>) => {
    const { table, status, density, noun, hasActiveFilters } = instance;
    const controller = useConstant(() =>
        createGridController(instance.virtualization?.initialRect?.width ?? 0),
    );
    const [rootElement, setRootElement] = useState<HTMLDivElement | null>(null);
    const [messageLayer, setMessageLayer] = useState<HTMLDivElement | null>(
        null,
    );
    const rootRef = useCallback(
        (element: HTMLDivElement | null) => {
            controller.setRoot(element);
            setRootElement(element);
        },
        [controller],
    );

    useTableHandle({ ref, instance, controller });
    useAutoFocus({ instance, controller, autoFocus });

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
            const isOwnTarget =
                event.target instanceof Node &&
                event.currentTarget.contains(event.target);
            if (
                !isOwnTarget ||
                event.defaultPrevented ||
                isEditableTarget(event.target)
            ) {
                return;
            }
            const pressed = normalizeKey(event);
            if (
                pressed.key === "/" &&
                !pressed.isMod &&
                controller.focusSearch
            ) {
                event.preventDefault();
                controller.focusSearch();
                return;
            }
            const { shortcuts } = instance.optionsRef.current;
            if (!shortcuts) {
                return;
            }
            const shortcut = shortcuts.find((candidate) =>
                matchesShortcut(candidate.key, pressed),
            );
            if (!shortcut) {
                return;
            }
            const focused = getFocusedAddress(table.atoms.cellSelection.get());
            const isHandled = shortcut.run({
                row: focused
                    ? table.getRowModel().rowsById[focused.rowId]
                    : undefined,
            });
            if (isHandled !== false) {
                event.preventDefault();
            }
        },
        [controller, instance.optionsRef, table],
    );

    const { isEmpty, isRefetching } = getStatusFlags({
        status,
        rowCount: table.getRowModel().rows.length,
    });

    const contextValue = useMemo(
        (): DataGridContextValue => ({
            status,
            density,
            noun,
            hasActiveFilters,
            isEmpty,
            clearFilters: () => {
                table.setGlobalFilter("");
                table.setColumnFilters([]);
            },
            controller,
            rootElement,
            messageLayer,
            visit: (visitor) => visitor(instance),
            renderGrid: () => (
                <Grid
                    instance={instance}
                    controller={controller}
                    rootElement={rootElement}
                    messageLayerRef={setMessageLayer}
                    ariaLabel={ariaLabel}
                    ariaLabelledby={ariaLabelledby}
                />
            ),
        }),
        [
            ariaLabel,
            ariaLabelledby,
            controller,
            density,
            hasActiveFilters,
            instance,
            isEmpty,
            messageLayer,
            noun,
            rootElement,
            status,
            table,
        ],
    );

    return (
        <DataGridContext.Provider value={contextValue}>
            <CellTooltipHost root={rootElement}>
                {/* Table-scoped shortcuts: keys bubble up from the focused grid. */}
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <div
                    ref={rootRef}
                    className={classNames(styles.root, className)}
                    data-dt-root=""
                    data-density={density}
                    style={styleWithVars({
                        "--dg-row-h": `${ROW_HEIGHT_BY_DENSITY[density]}px`,
                        "--dg-header-row-h": `${HEADER_ROW_HEIGHT}px`,
                    })}
                    data-grid-lines={instance.gridLines}
                    data-list-table={instance.hasListEditing || undefined}
                    data-column-layout={
                        instance.columnLayout === "center"
                            ? "center"
                            : undefined
                    }
                    data-top-border={
                        topBorder === "divider" ? "divider" : undefined
                    }
                    data-status={status.state}
                    data-empty={isEmpty || undefined}
                    data-refetching={isRefetching || undefined}
                    data-motion={
                        instance.motion.level === "full"
                            ? undefined
                            : instance.motion.level
                    }
                    onKeyDown={handleKeyDown}
                >
                    {children ?? (
                        <>
                            <GridSlot />
                            <DataGridEmpty />
                            <DataGridError />
                        </>
                    )}
                </div>
            </CellTooltipHost>
        </DataGridContext.Provider>
    );
};

/** The scroll viewport: header, rows, skeleton, and refetch bar. */
const GridSlot = () => useDataGridContext().renderGrid();

interface SlotProps {
    className?: string;
    children?: ReactNode;
}

/** Bar above the grid for search, filters, and bulk actions. */
const DataGridToolbar = ({ className, children }: Readonly<SlotProps>) => (
    <div className={classNames(styles.toolbar, className)}>{children}</div>
);

/** Bar below the grid for counts and paging. */
const DataGridFooter = ({ className, children }: Readonly<SlotProps>) => (
    <div className={classNames(styles.footer, className)}>{children}</div>
);

/**
 * Design-system data table. Build the instance with `useDataGrid`, then
 * compose parts:
 *
 * @example
 * <DataGrid table={table} aria-label="Statements">
 *     <DataGrid.Toolbar>
 *         <DataGrid.Search />
 *         <DataGrid.FilterChips />
 *     </DataGrid.Toolbar>
 *     <DataGrid.Grid />
 *     <DataGrid.Empty>No statements yet</DataGrid.Empty>
 *     <DataGrid.Error />
 * </DataGrid>
 */
export const DataGrid = Object.assign(DataGridRoot, {
    Grid: GridSlot,
    Empty: DataGridEmpty,
    Error: DataGridError,
    Toolbar: DataGridToolbar,
    Footer: DataGridFooter,
    Search: DataGridSearch,
    FilterChips: DataGridFilterChips,
    Pager: DataGridPager,
    ColumnSettings: DataGridColumnSettings,
    SelectAllMatching: DataGridSelectAllMatching,
    Gate: DataGridGate,
    RangeAction: DataGridRangeActionSlot,
});
